import { useCallback, useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/index.js';
import {
  PeerTypes,
  PeersResponse,
  useBulkFulfillPeerPrizes,
  useGetPeerPrizes,
  useListCampaignPeers,
  useShowCampaignSettings,
} from '#api/index.js';
import { CampaignPrize } from '#api/new/types/prizes.js';
import { useCampaignPeers } from '#crowdfunding/pages/Management/hooks/hooks.js';
import { useFormyValidator } from '@givebacks/formy';
import { getErrorMessage_v2, toTitleCase } from 'core/Utils';
import { Button, Group, Modal, ModalProps, Stack, Text, ThemeIcon, Title, notifications } from 'ui';
import { IconCircleCheck } from 'ui/Icons';

import { SelectedPrizesForm, SelectedPrizesFormType } from './SelectedPrizesForm';

interface FulfillPrizesModalProps extends ModalProps {
  isAll?: boolean;
  peerType: PeerTypes;
  peerIds?: string[];
  peerName?: string;
  earnedPrizes?: CampaignPrize[];
}

const getParticipants = (data?: PeersResponse<'participants'>) => {
  return data?.participants;
};

const getTeams = (data?: PeersResponse<'teams'>) => {
  return data?.teams;
};

const FulfillPrizesModalFooter = ({
  onClose,
  peerType,
  peerIds,
  isAll,
  peerName,
}: {
  onClose: () => void;
  peerType: PeerTypes;
  peerIds?: string[];
  isAll?: boolean;
  peerName?: string;
}) => {
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { useGetForm, validateForms } = useFormyValidator<SelectedPrizesFormType>();

  const form = useGetForm('selectedPrizes');
  const prizeIds = form?.values?.prizes;

  const { data: participants } = useListCampaignPeers({
    peer_type: 'participants',
    causeId,
    campaignId,
    select: getParticipants,
  });

  const { data: teams } = useListCampaignPeers({
    peer_type: 'teams',
    causeId,
    campaignId,
    select: getTeams,
  });

  const finalPeerIds = useMemo(() => {
    // If peerIds is provided, use it
    if (peerIds) {
      return peerIds;
    }
    // Otherwise, use all peers for bulk fulfillment
    return peerType === 'teams'
      ? teams?.map((team) => team.uuid)
      : participants?.map((participant) => participant.uuid);
  }, [peerType, teams, participants, peerIds]);

  const { mutate: bulkFulfillTeamsPrizes, isPending } = useBulkFulfillPeerPrizes({ peerType });

  const successMessage = useMemo(() => {
    if (isAll) {
      return `Prizes were successfully marked as fulfilled for all ${peerType}`;
    }
    if (peerName) {
      return `Prizes were successfully marked as fulfilled for ${peerName}`;
    }
    return `Prizes were successfully marked as fulfilled for ${finalPeerIds?.length} ${peerType}`;
  }, [isAll, peerName, finalPeerIds?.length, peerType]);

  const handleMarkAllPrizesFulfilled = useCallback(() => {
    if (!causeId || !campaignId) return;
    if (validateForms().isValid) {
      bulkFulfillTeamsPrizes(
        {
          causeId,
          campaignId,
          peerIds: finalPeerIds || [],
          prizeIds: prizeIds || [],
        },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Prizes fulfilled!',
              message: successMessage,
              variant: 'success',
            });
            onClose();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error, 'Error marking prizes as fulfilled'),
              variant: 'error',
            });
          },
        }
      );
    }
  }, [
    bulkFulfillTeamsPrizes,
    campaignId,
    causeId,
    finalPeerIds,
    onClose,
    prizeIds,
    successMessage,
    validateForms,
  ]);

  return (
    <Modal.AffixedFooter>
      <Group justify="flex-end">
        <Button variant="subtle" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button size="md" onClick={handleMarkAllPrizesFulfilled} loading={isPending}>
          Fulfill Prizes
        </Button>
      </Group>
    </Modal.AffixedFooter>
  );
};

const FulfillPrizesModalContent = ({
  peerType,
  peerIds,
  peerName,
  isAll,
  earnedPrizes,
}: {
  peerType: PeerTypes;
  peerIds?: string[];
  peerName?: string;
  isAll?: boolean;
  earnedPrizes?: CampaignPrize[];
}) => {
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { data: campaignSettings } = useShowCampaignSettings({
    causeId,
    campaignId,
  });

  const { data: prizes } = useGetPeerPrizes({
    causeId,
    campaignId,
    peerType,
  });

  const prizeData = useMemo(() => {
    const prizesToFulfill = earnedPrizes ?? prizes;

    const filteredPrizes = prizesToFulfill?.filter((prize) => !prize.type.includes('Top'));

    // If campaign is read-only, use all prizes. Otherwise, use the filtered prizes.
    const prizeData = campaignSettings?.isCampaignReadOnly ? prizesToFulfill : filteredPrizes;

    return prizeData?.map((prize) => prize.uuid);
  }, [campaignSettings?.isCampaignReadOnly, earnedPrizes, prizes]);

  const title = useMemo(() => {
    if (isAll) {
      return `You're about to mark all earned prizes as fulfilled for all ${peerType}.`;
    }
    if (peerName) {
      return `You're about to mark earned prizes as fulfilled for ${peerName}.`;
    }
    if (peerIds && peerIds.length) {
      return `You're about to mark earned prizes as fulfilled for ${peerIds.length} ${peerType}.`;
    }
    return `You're about to mark earned prizes as fulfilled for ${
      peerIds?.length || 1
    } ${peerType}.`;
  }, [peerIds, isAll, peerType, peerName]);

  if (!peerType) return null;

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={4}>{title}</Title>
        <Group gap="xs">
          <Text fz="sm" c="gray">
            All fulfilled prizes will show as
          </Text>
          <ThemeIcon color="mint.7" size="sm" variant="transparent">
            <IconCircleCheck />
          </ThemeIcon>
          <Text fz="sm" c="gray">
            status.
          </Text>
        </Group>
      </Stack>
      <SelectedPrizesForm
        peerType={peerType}
        initialPrizes={prizeData}
        filteredPrizes={earnedPrizes}
      />
    </Stack>
  );
};

const FulfillPrizesModal = ({
  opened,
  onClose,
  isAll,
  peerType,
  peerIds,
  peerName,
  earnedPrizes,
}: FulfillPrizesModalProps) => {
  const { peer_type } = useCampaignPeers();

  const title = useMemo(() => {
    if (isAll) {
      return `Fulfill Prizes for All ${toTitleCase(peer_type ?? '')}`;
    }
    if (peerName) {
      return `Fulfill Prizes for ${peerName || toTitleCase(peer_type ?? '')}`;
    }
    if (peerIds && peerIds.length) {
      return `Fulfill Prizes for ${peerIds.length} ${toTitleCase(peer_type ?? '')}`;
    }
    return `Fulfill Prizes for ${toTitleCase(peer_type ?? '')}`;
  }, [peerIds, peerName, isAll, peer_type]);

  return (
    <Modal opened={opened} onClose={onClose} size="lg" title={title}>
      <FulfillPrizesModalContent
        peerType={peerType}
        peerIds={peerIds}
        peerName={peerName}
        isAll={isAll}
        earnedPrizes={earnedPrizes}
      />
      <FulfillPrizesModalFooter
        onClose={onClose}
        peerType={peerType}
        peerIds={peerIds}
        isAll={isAll}
        peerName={peerName}
      />
    </Modal>
  );
};

export { FulfillPrizesModal };
